"use client";

import type { FC, ReactNode } from "react";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import api from "@/app/_module/services/api";
import { getSupabaseSession } from "@/app/_module/services/auth";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import type { GenericResponse } from "@/app/_module/services/generalTypes";
import type { AgencyWithRole, UserProfile } from "@/app/auth/_modules/types/user";
import { userServices } from "@/app/auth/sign-in/_modules/services/user-services";

import { UserContext } from "./userContexts";
import { initialUserState } from "./userState";
import { isDemoHostname } from "@/app/_module/utils/demoMode";

interface Iprop {
  children: ReactNode;
}

// Helper function to get agency data from localStorage
const getAgencyFromLocalStorage = (): AgencyWithRole | null => {
  if (typeof window === "undefined") return null;
  
  try {
    const agencyData = sessionStorage.getItem("insuracentral_current_agency");
    if (agencyData) {
      const parsed = JSON.parse(agencyData);
      
      // Check if data is in flat AgencyData structure (from AgencyTokenManager)
      if (parsed && !parsed.agency && parsed.id && parsed.name && parsed.role !== undefined) {
        return {
          agency: {
            id: parsed.id,
            name: parsed.name,
            description: parsed.description ?? "",
            contactEmail: parsed.contactEmail ?? "",
            isActive: parsed.isActive ?? true,
            parentAgencyId: parsed.parentAgencyId ?? undefined,
            createdAt: parsed.createdAt ?? new Date().toISOString(),
            updatedAt: parsed.updatedAt ?? new Date().toISOString(),
            deletedAt: parsed.deletedAt ?? null,
          },
          role: parsed.role ?? "",
          permissions: parsed.permissions ?? [],
        } as AgencyWithRole;
      }
      
      // Check if data is already in AgencyWithRole structure
      if (parsed && parsed.agency && parsed.role !== undefined) {
        return parsed as AgencyWithRole;
      }

      // Anything else is NOT an AgencyWithRole, so don't pretend it is.
      //
      // This used to `return parsed as AgencyWithRole` for any shape at all, which put an
      // object with no `.agency` into the user context. Consumers reasonably write
      // `user.currentAgency?.agency.id` — optional on currentAgency, because "no agency"
      // is expected, but not on `.agency`, because an AgencyWithRole always has one. The
      // result was a hard runtime crash ("Cannot read properties of undefined (reading
      // 'id')") on the agency dashboard, from stale or half-written storage rather than
      // anything wrong with the account.
      //
      // Returning null instead means the caller sees "no agency selected", which every
      // consumer already handles, and the value gets refetched from the API.
      return null;
    }
  } catch (error) {
    // Silent fail - unable to parse agency data
  }
  
  return null;
};

export const UserProvider: FC<Iprop> = function ({ children }) {
  const [user, setUser] = useState(initialUserState);
  const [autoAgencyTried, setAutoAgencyTried] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const pathname = usePathname();
  const wasOnAuthPageRef = useRef(false);
  const hasFetchedRef = useRef(false);
  // Bumping this re-runs the profile effect. See the recovery effect below.
  const [profileRetryTick, setProfileRetryTick] = useState(0);

  // Set agency data immediately when component mounts
  useEffect(() => {
    const currentAgency = getAgencyFromLocalStorage();
    if (currentAgency) {
      setUser(prev => ({
        ...prev,
        currentAgency,
      }));
    }
    // Listen for agency changes set by AgencyTokenManager
    const onAgencyChange = () => {
      const latest = getAgencyFromLocalStorage();
      if (latest) {
        setUser(prev => ({ ...prev, currentAgency: latest }));
      }
    };
    
    // Listen for storage changes (when agency is updated in another tab)
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === 'insuracentral_current_agency' && e.newValue) {
        try {
          const agencyData = JSON.parse(e.newValue);
          if (agencyData) {
            setUser(prev => ({ ...prev, currentAgency: agencyData }));
          }
        } catch (error) {
          // Silent fail - unable to parse agency data from storage event
        }
      }
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('insuracentral-agency-change', onAgencyChange as EventListener);
      window.addEventListener('storage', onStorageChange as EventListener);
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('insuracentral-agency-change', onAgencyChange as EventListener);
        window.removeEventListener('storage', onStorageChange as EventListener);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounts
    const isOnAuthPage = pathname?.startsWith("/auth");
    const isOnVendorPage = pathname?.startsWith("/vendor");
    const isOnMarketingPage = pathname?.startsWith("/marketing");
    // Public QR hand-off page — the scanning phone is anonymous by design
    const isOnSharePage = pathname === "/share" || pathname?.startsWith("/share/");
    // Agent landing pages use subdomains
    // The middleware rewrites to /agent-landing/ server-side, but usePathname() returns the browser path (/)
    // So we detect by hostname instead
    const isOnAgentLandingPage = typeof window !== 'undefined' && (() => {
      const h = window.location.hostname;
      // demo.insuracentral.com is a FIRST-CLASS APP HOST, not an agent landing page.
      // Without this exclusion the sandbox host matched the "any subdomain" rule below,
      // so this provider skipped the profile fetch entirely — user.user.id stayed
      // undefined forever, ViewerModeGuard's `!user?.user?.id` guard returned null, and
      // every demo page rendered with a working sidebar and a COMPLETELY BLANK body.
      if (isDemoHostname(h)) return false;
      if (h.endsWith('.insuracentral.com') && h !== 'app.insuracentral.com' && h !== 'www.insuracentral.com' && h !== 'insuracentral.com') return true;
      if (h.endsWith('.localhost') && !h.startsWith('app.')) return true;
      return false;
    })();

    // Track if we were on auth page
    if (isOnAuthPage) {
      wasOnAuthPageRef.current = true;
    }

    // Skip fetch if we're on auth, vendor, marketing, or agent landing pages - no Supabase user data needed
    // Vendor portal has its own authentication system (API key + MFA)
    // Marketing portal has its own JWT auth system (MarketingSessionGuard)
    // Agent landing pages are public, no auth required
    if (isOnAuthPage || isOnVendorPage || isOnMarketingPage || isOnAgentLandingPage || isOnSharePage) {
      if (isOnVendorPage || isOnMarketingPage || isOnAgentLandingPage || isOnSharePage) {
        setIsInitializing(false);
      }
      return;
    }

    // If we already fetched successfully and didn't come from auth page, skip
    // But if we just transitioned from auth page, we need to re-fetch
    if (hasFetchedRef.current && !wasOnAuthPageRef.current) {
      return;
    }

    async function fetchProfile() {
      try {

        // Check for Supabase session - ONLY use ES256 tokens
        const supabaseSession = await getSupabaseSession();

        if (!supabaseSession?.access_token) {
          // Don't return early - still set agency data if available
          const currentAgency = getAgencyFromLocalStorage();
          if (currentAgency && isMounted) {
            setUser(prev => ({
              ...prev,
              currentAgency,
            }));
          }
          // Still need to set initialization as complete even if no session
          if (isMounted) {
            // No token to ask with is "unknown", not "unregistered" — during an
            // auth outage getSupabaseSession() returns null for a live user.
            setProfileLoadFailed(true);
            setIsInitializing(false);
          }
          return;
        }

        // Use the access token from Supabase session for API calls
        const res = await handleApiCall<GenericResponse<typeof initialUserState>>(async () => {
          const response = await api.get<GenericResponse<typeof initialUserState>>({ url: getApiUrl(endpoint.user.getProfile) });
          return response;
        });

        // Only update state if component is still mounted
        if (!isMounted) return;

        // handleApiCall() throws on any non-success, so reaching this line means
        // the backend actually answered. Whatever it said is now authoritative.
        setProfileLoadFailed(false);

        // Unwrap common backend shapes
        const rawUnknown: unknown = res as unknown;
        const root = (rawUnknown && typeof rawUnknown === 'object' && 'data' in (rawUnknown as Record<string, unknown>)
          ? ((rawUnknown as { data?: unknown }).data)
          : rawUnknown);
        const payload = (root && typeof root === 'object' && 'data' in (root as Record<string, unknown>)
          ? ((root as { data?: unknown }).data)
          : root) as typeof initialUserState | undefined;

        // Get agency data from sessionStorage (in case it changed)
        const currentAgency = getAgencyFromLocalStorage();

        const payloadAgencyUnknown = (payload && typeof payload === 'object' && 'currentAgency' in (payload as unknown as Record<string, unknown>))
          ? (payload as { currentAgency?: unknown }).currentAgency
          : undefined;
        const payloadAgency = (payloadAgencyUnknown && typeof payloadAgencyUnknown === 'object' && 'agency' in (payloadAgencyUnknown as Record<string, unknown>))
          ? (payloadAgencyUnknown as AgencyWithRole)
          : undefined;

        // Extract agency ID from enriched user object
        const enrichedUser = (payload as any)?.enrichedUser;

        // Use agencyId directly from enriched user object (backend now provides this correctly)
        const agencyId = enrichedUser?.agencyId;
        
        // Only use sessionStorage fallback if enriched user doesn't have agencyId (shouldn't happen now)
        if (!agencyId) {
          try {
            const userContext = sessionStorage.getItem('insuracentral_user_context');
            if (userContext) {
              const parsed = JSON.parse(userContext);
              // Note: We don't set agencyId here since it should come from enriched user
            }
          } catch (error) {
            // Silent fail - unable to parse user context for agency ID fallback
          }
        }
        
        if (agencyId) {
          // Always update AgencyTokenManager with the latest agency data from enriched user
          const { default: AgencyTokenManager } = await import('../../../services/agencyTokenManager');

          // Read stored agency data BEFORE clearing — clearAgencyToken() wipes sessionStorage
          const currentStoredAgencyId = AgencyTokenManager.getCurrentAgencyId();
          const storedAgencyData = AgencyTokenManager.getCurrentAgency();

          if (currentStoredAgencyId && currentStoredAgencyId !== agencyId) {
            // Clear existing agency token as it's for wrong agency
            AgencyTokenManager.clearAgencyToken();
          }

          // Preserve the agency name: prefer in-memory state, then stored data (read before clear),
          // then enriched user from backend, then fall back to empty string (never show "Agency")
          const preservedAgencyName = currentAgency?.agency?.name ||
                                       storedAgencyData?.name ||
                                       enrichedUser?.agencyName ||
                                       '';

          AgencyTokenManager.setCurrentAgency({
            id: agencyId,
            name: preservedAgencyName,
            description: currentAgency?.agency?.description || '',
            contactEmail: currentAgency?.agency?.contactEmail || '',
            isActive: true,
            role: enrichedUser?.agencyRole || currentAgency?.role || 'OWNER',
            permissions: currentAgency?.permissions || [],
            createdAt: currentAgency?.agency?.createdAt || new Date().toISOString(),
            updatedAt: currentAgency?.agency?.updatedAt || new Date().toISOString(),
            deletedAt: null,
          });

          // CRITICAL FIX: Removed duplicate event dispatch
          // AgencyTokenManager.setCurrentAgency() already dispatches 'insuracentral-agency-change'
          // when agency actually changes. Dispatching it again here causes infinite loops.
        }

        // Build user object with enriched data
        const payloadUser = (payload as any)?.user || {};
        
        const userData = {
          ...(payload as typeof initialUserState),
          // Extract notificationPreferences from payload.user to the root level
          // Backend puts it inside safeUser (response.user), but UserProfile expects it at root
          notificationPreferences: payloadUser.notificationPreferences || (payload as any).notificationPreferences || undefined,
          user: {
            ...payloadUser,
            // Override with enriched user data if available
            ...(enrichedUser ? {
              // Basic identification
              id: enrichedUser.userId || enrichedUser.id,
              userId: enrichedUser.userId || enrichedUser.id,
              
              // User type and account information
              userType: enrichedUser.userType,
              accountType: enrichedUser.accountType,
              accountStatus: enrichedUser.accountStatus || 'active',
              
              // Admin flags
              isSystemAdmin: enrichedUser.isSystemAdmin || false,
              
              // Trial information
              isTrialUser: enrichedUser.isTrialUser || false,
              isTrialActive: enrichedUser.isTrialActive || false,
              isTrialExpired: enrichedUser.isTrialExpired || false,
              trialExpiresAt: enrichedUser.trialExpiresAt,
              trialEnd: enrichedUser.trialEnd,
              trialStartDate: enrichedUser.trialStartDate,
              trialScheduledStart: enrichedUser.trialScheduledStart,
              hasUsedTrial: enrichedUser.hasUsedTrial || false,
              trialUpgradeOptIn: enrichedUser.trialUpgradeOptIn || false,
              trialAccountTypeUsed: enrichedUser.trialAccountTypeUsed,
              voiceProvider: enrichedUser.voiceProvider ?? payloadUser.voiceProvider,
              nextDialerAvailable: enrichedUser.nextDialerAvailable ?? payloadUser.nextDialerAvailable ?? false,
              needsTrialUpgrade: enrichedUser.needsTrialUpgrade || false,
              canUpgradeTrial: enrichedUser.canUpgradeTrial || false,
              trialStatus: enrichedUser.trialStatus || 'not_trial',
              
              // User profile information
              name: enrichedUser.name,
              email: enrichedUser.email,
              phone: enrichedUser.phone,
              contactPhone: enrichedUser.contactPhone,
              defaultOutboundPhone: enrichedUser.defaultOutboundPhone,
              title: enrichedUser.title,
              city: enrichedUser.city,
              profileImagePath: enrichedUser.profileImagePath,
              profileImageUrl: enrichedUser.profileImageUrl,
              
              // Verification status
              emailVerified: enrichedUser.emailVerified || false,
              phoneVerified: enrichedUser.phoneVerified || false,
              emailVerifiedAt: enrichedUser.emailVerifiedAt,
              
              // Account flags
              isActive: enrichedUser.isActive !== false,
              isTestUser: enrichedUser.isTestUser || false,
              isOnline: enrichedUser.isOnline || false,
              showOnLeaderboard: enrichedUser.showOnLeaderboard !== false,
              hideNameOnLeaderboard: enrichedUser.hideNameOnLeaderboard === true,
              
              // Professional information
              npn: enrichedUser.npn,
              compLevel: enrichedUser.compLevel,
              upLine: enrichedUser.upLine,
              sockId: enrichedUser.sockId,
              
              // Subscription and billing
              stripeId: enrichedUser.stripeId,
              selectedPlan: enrichedUser.selectedPlan,
              stripePaymentMethodId: enrichedUser.stripePaymentMethodId,
              autoBillingEnabled: enrichedUser.autoBillingEnabled || false,
              billingPreferenceUpdatedAt: enrichedUser.billingPreferenceUpdatedAt,
              subEnd: enrichedUser.subEnd,
              
              // Activity tracking
              lastLoginAt: enrichedUser.lastLoginAt,
              lastSeenAt: enrichedUser.lastSeenAt,
              lastActivityIp: enrichedUser.lastActivityIp,
              lastLoginIp: enrichedUser.lastLoginIp,
              registrationIp: enrichedUser.registrationIp,
              
              // Feature flags and settings
              leadsLight: enrichedUser.leadsLight || false,
              weeklyRep: enrichedUser.weeklyRep,
              dailyImportsCount: enrichedUser.dailyImportsCount || 0,
              lastImportResetDate: enrichedUser.lastImportResetDate,
              
              // MFA and security settings
              // Compute mfaEnabled from mfaSettings if not explicitly set
              mfaEnabled: enrichedUser.mfaEnabled ||
                         enrichedUser.mfaSettings?.totpEnabled ||
                         enrichedUser.mfaSettings?.smsEnabled ||
                         false,
              mfaSettings: enrichedUser.mfaSettings || payloadUser.mfaSettings || {},
              // MFA setup reminder for banner (requires BOTH TOTP and SMS)
              // This comes from the backend's userData.mfaSetupReminder (payloadUser), not enrichedUser
              mfaSetupReminder: payloadUser.mfaSetupReminder || enrichedUser.mfaSetupReminder || null,
              
              // SMS settings
              smsSettings: enrichedUser.smsSettings || {},
              
              // Privacy settings
              privacySettings: enrichedUser.privacySettings || {},
              
              // Account creation and approval
              createdBy: enrichedUser.createdBy,
              accountApprovedAt: enrichedUser.accountApprovedAt,
              reissueEnabledAt: enrichedUser.reissueEnabledAt,
              callForwardingEnabledAt: enrichedUser.callForwardingEnabledAt,
              
              // Agency information
              agencyId: enrichedUser.agencyId,
              agencyRole: enrichedUser.agencyRole,
              needsAgencyCreation: enrichedUser.needsAgencyCreation || false,
            } : {}),
            // CRITICAL: Also read trialAccountTypeUsed directly from payload.user if not in enrichedUser
            // This ensures we get the value even if enrichedUser is not in the response
            ...(payloadUser.trialAccountTypeUsed && !enrichedUser?.trialAccountTypeUsed ? {
              trialAccountTypeUsed: payloadUser.trialAccountTypeUsed,
            } : {}),
            ...(payloadUser.trial_account_type_used && !payloadUser.trialAccountTypeUsed && !enrichedUser?.trialAccountTypeUsed ? {
              trialAccountTypeUsed: payloadUser.trial_account_type_used,
            } : {}),
            // Before approval the row has no trialAccountTypeUsed yet — only the
            // plan picked at signup. A Lite signup must read as Lite from the
            // first page load, or the dialer surfaces show until approval.
            ...((() => {
              const known = enrichedUser?.trialAccountTypeUsed || payloadUser.trialAccountTypeUsed || payloadUser.trial_account_type_used;
              const type = String(enrichedUser?.accountType || payloadUser.accountType || payloadUser.account_type || '').toLowerCase();
              const picked = enrichedUser?.selectedPlan || payloadUser.selectedPlan || payloadUser.selected_plan;
              return !known && type === 'trial' && picked ? { trialAccountTypeUsed: picked, selectedPlan: picked } : {};
            })()),
            // userStatus: read from enrichedUser first (guard fetches from DB), then fallback to payloadUser
            userStatus: enrichedUser?.userStatus || payloadUser.userStatus || payloadUser.user_status || 'available',
            lastManualStatusChangeAt: enrichedUser?.lastManualStatusChangeAt || payloadUser.lastManualStatusChangeAt || payloadUser.last_manual_status_change_at,
          },
          currentAgency: currentAgency || payloadAgency || undefined,
        };

        setUser(userData);

        // Store agency ID in sessionStorage for axios interceptor fallback
        // Note: The primary source is AgencyTokenManager.getCurrentAgencyId()
        if (agencyId) {
          try {
            sessionStorage.setItem('insuracentral_user_context', JSON.stringify({
              agencyId: agencyId,
              agencyRole: enrichedUser?.agencyRole,
              userType: enrichedUser?.userType,
              accountType: enrichedUser?.accountType,
            }));
          } catch (error) {
            // Silent fail - unable to store agency context
          }
        }
      } catch (error) {
        // Optionally handle error (e.g., show toast, log out, etc.)

        // Even if API call fails, we can still set agency data from localStorage
        if (isMounted) {
          // Mark the profile as UNKNOWN rather than absent, so ApprovalStatusGuard
          // does not read a transient failure as an unregistered user and evict
          // the session. The bounded retry below is what actually recovers it.
          setProfileLoadFailed(true);
          const currentAgency = getAgencyFromLocalStorage();
          if (currentAgency) {
            setUser(prev => ({
              ...prev,
              currentAgency,
            }));
          }
        }
      } finally {
        // Always set initialization as complete
        if (isMounted) {
          setIsInitializing(false);
          hasFetchedRef.current = true;
          wasOnAuthPageRef.current = false; // Reset after successful fetch
        }
      }
    }

    void fetchProfile();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [pathname, profileRetryTick]); // Re-run on navigation, and on an explicit retry

  // ── RECOVER A FAILED PROFILE FETCH ────────────────────────────────────────
  //
  // fetchProfile() swallows its error and the `finally` clears isInitializing, so a
  // failed fetch leaves user.user.id undefined FOREVER — and ViewerModeGuard renders
  // `null` on exactly that condition. The result is a signed-in user staring at a blank
  // page with no error, no spinner and no retry, which a manual refresh fixes.
  //
  // The fetch that fails is almost always the MFA gate refusing during the login window:
  // /users/profile is gated, and on a first login the session can still be unstamped when
  // the dashboard loads. The backend fix closes that window, but this is the belt to its
  // braces — ANY transient failure (cold start, dropped connection) had the same
  // permanent consequence.
  //
  // Two triggers, mirroring the dialer's own recovery in TwilioDeviceContext:
  //   1. 'mfa-satisfied' — the axios layer fires it the moment the gate stops refusing.
  //   2. a short bounded retry, for failures that have nothing to do with MFA.
  //
  // Guarded on a missing user id, so a healthy session never refetches.
  useEffect(() => {
    if (isInitializing) return;          // still loading — not a failure yet
    if (user?.user?.id) return;          // healthy — nothing to recover
    if (typeof window === 'undefined') return;
    if (window.location.pathname.startsWith('/auth/')) return;

    let attempts = 0;
    const MAX_ATTEMPTS = 4;
    const RETRY_MS = 3000;

    const retry = () => {
      hasFetchedRef.current = false;     // allow the effect's own skip-guard to pass
      setProfileRetryTick((n) => n + 1);
    };

    const onMfaSatisfied = () => retry();
    window.addEventListener('mfa-satisfied', onMfaSatisfied);

    const timer = setInterval(() => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        clearInterval(timer);
        return;
      }
      retry();
    }, RETRY_MS);

    return () => {
      window.removeEventListener('mfa-satisfied', onMfaSatisfied);
      clearInterval(timer);
    };
  }, [isInitializing, user?.user?.id]);

  // Do not auto-select agencies; selection must be manual in the sign-in flow
  useEffect(() => {
    if (autoAgencyTried) return;
    setAutoAgencyTried(true);
  }, []); // Empty dependency array to run only once

  const updateCurrentAgency = useCallback((agency: {
    id: string;
    name?: string;
    role?: string;
    description?: string;
    contactEmail?: string;
    isActive?: boolean;
    parentAgencyId?: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string | null;
    permissions?: string[];
  }) => {
    const agencyData = {
      agency: {
        id: agency.id,
        name: agency.name || '',
        description: agency.description || '',
        contactEmail: agency.contactEmail || '',
        isActive: agency.isActive ?? true,
        parentAgencyId: agency.parentAgencyId,
        createdAt: agency.createdAt || new Date().toISOString(),
        updatedAt: agency.updatedAt || new Date().toISOString(),
        deletedAt: agency.deletedAt ?? null,
      },
      role: agency.role || '',
      permissions: agency.permissions || [],
    };

    // IMPORTANT: Update AgencyTokenManager so API calls use the correct agency ID
    if (typeof window !== 'undefined') {
      try {
        // Update AgencyTokenManager with flat structure
        const { AgencyTokenManager } = require('@/app/_module/services/agencyTokenManager');
        AgencyTokenManager.setCurrentAgency({
          id: agency.id,
          name: agency.name || '',
          description: agency.description || '',
          contactEmail: agency.contactEmail || '',
          isActive: agency.isActive ?? true,
          parentAgencyId: agency.parentAgencyId,
          createdAt: agency.createdAt || new Date().toISOString(),
          updatedAt: agency.updatedAt || new Date().toISOString(),
          deletedAt: agency.deletedAt ?? null,
          role: agency.role || '',
          permissions: agency.permissions || [],
        });

        // Also save nested structure for backward compatibility
        sessionStorage.setItem('insuracentral_current_agency', JSON.stringify(agencyData));
      } catch (error) {
        // Error saving agency to sessionStorage - agency updates still applied to state
      }
    }

    setUser(prev => ({
      ...prev,
      currentAgency: agencyData,
    }));
  }, []);

  // Optimized update function that doesn't trigger full refresh
  const updateUserContext = useCallback((updater: (prev: UserProfile) => UserProfile) => {
    setUser(updater);
  }, []);

  // Specific function for agency updates that doesn't trigger initialization
  const updateAgencyOnly = useCallback((agencyData: AgencyWithRole) => {
    // DO NOT write to 'insuracentral_current_agency' - that's managed by AgencyTokenManager
    // Writing to it causes conflicts and clears the agency ID
    // The user context agency is kept in React state only

    setUser(prev => ({
      ...prev,
      currentAgency: agencyData,
    }));

    // DO NOT dispatch agency change event here - it's already dispatched by AgencyTokenManager
    // Dispatching it again causes double invalidation and race conditions
  }, []);

  // Memoize user properties to prevent unnecessary re-renders
  const memoizedUser = useMemo(() => user, [
    user?.user?.id,
    user?.user?.userType,
    user?.user?.email,
    user?.user?.name,
    (user?.user as any)?.title,
    (user?.user as any)?.profileImageUrl,
    (user?.user as any)?.contactPhone,
    user?.currentAgency?.agency?.id,
    user?.currentAgency?.role,
    user?.currentAgency?.agency?.name,
    (user?.user as any)?.userStatus,
    (user?.user as any)?.isOnline,
    (user?.user as any)?.showOnLeaderboard, // Include for leaderboard visibility banner updates
    user?.notificationPreferences?.shouldReceiveEmail,
    user?.notificationPreferences?.shouldReceiveSms,
    user?.notificationPreferences?.shouldReceiveLeadAssignments,
    user?.notificationPreferences?.shouldReceiveUpcomingAppointments,
    user?.notificationPreferences?.shouldReceiveWeeklyReports,
    // App preferences that gate UI visibility across pages (DialIQ, etc.)
    // Without this, toggling prefs in Settings doesn't propagate without a hard refresh.
    (user?.user as any)?.appPreferences?.dialIq?.enabled,
    // The rest of appPreferences too (table columns, door knocking, …): a
    // Settings toggle wrote the new value but every consumer kept the memoized
    // user until an unrelated field changed, so the checkbox never moved.
    JSON.stringify((user?.user as any)?.appPreferences ?? null),
    isInitializing
  ]);

  const value = useMemo(() => {
    return { user: memoizedUser, refetchUser: updateUserContext, updateCurrentAgency, updateAgencyOnly, isInitializing, profileLoadFailed };
  }, [memoizedUser, updateUserContext, updateCurrentAgency, updateAgencyOnly, isInitializing, profileLoadFailed]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
